import {Box, Button, Typography} from "@mui/material";
import React from "react";
import {
  FileField,
  FileInput,
  Form,
  FormDataConsumer,
  required,
  SelectInput,
  TextInput,
  useCreate,
  useDelete,
  useNotify,
  useRefresh,
  useUpdate
} from "react-admin";
import Modal from "components/Modal";
import {MATERIAL_TYPE} from "types/index";
import {MATERIAL_TYPE_TITLE} from "../../../../constants";
import LoadingButton from "@mui/lab/LoadingButton";
import {validateSpace} from "utils/index";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const IMAGE_ACCEPT = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/svg+xml": [".svg"],
};
const ALLOWED_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".svg"];

enum CATEGORY_FILE_TYPE {
  BANNER_DESKTOP = "BANNER_DESKTOP",
  BANNER_MOBILE = "BANNER_MOBILE",
  REGULAR = "REGULAR",
}

type FileInputValue = {
  id?: string;
  rawFile?: File;
  src?: string;
  title?: string;
}

type CategoryFileValue = FileInputValue | null | undefined;

type CategoryImage = {
  id?: string;
  categoryFileId?: string;
  fileType?: CATEGORY_FILE_TYPE;
  file?: {
    url?: string;
    src?: string;
    name?: string;
    title?: string;
  };
  url?: string;
  src?: string;
  name?: string;
  title?: string;
}

interface EditCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  category?: {
    id: string;
    title: string;
    type?: MATERIAL_TYPE;
    files?: CategoryImage[];
    desktopBannerImage?: CategoryImage | null;
    mobileBannerImage?: CategoryImage | null;
  };
}

const validateImage = (value: CategoryFileValue) => {
  const file = value?.rawFile;
  if (!file) return undefined;

  const hasValidExtension = ALLOWED_IMAGE_EXTENSIONS.some((extension) =>
    file.name.toLowerCase().endsWith(extension)
  );
  const hasValidType = Object.keys(IMAGE_ACCEPT).includes(file.type);

  if (!hasValidExtension || (!hasValidType && file.type !== "")) {
    return "Допустимые форматы: JPG/JPEG, PNG, WEBP, SVG";
  }

  if (file.size > MAX_IMAGE_SIZE) {
    return "Максимальный размер файла: 5 МБ";
  }

  return undefined;
};

const prepareFileInputValue = (file?: CategoryImage | null): CategoryFileValue => {
  const src = file?.file?.url || file?.file?.src || file?.url || file?.src;
  if (!src) return undefined;

  return {
    id: file?.id || file?.categoryFileId,
    src,
    title: file?.file?.name || file?.file?.title || file?.name || file?.title || "Баннер",
  };
};

const hasFileChanges = (
  currentValue: CategoryFileValue,
  initialValue: CategoryFileValue
) => Boolean(currentValue?.rawFile) || (Boolean(initialValue) && !currentValue);

const prepareCategoryFileFormData = (
  currentValue: CategoryFileValue,
  fileType: CATEGORY_FILE_TYPE,
) => {
  if (!currentValue?.rawFile) return null;

  const formData = new FormData();

  formData.append("file", currentValue.rawFile);
  formData.append("fileType", fileType);
  return formData;
};

const EditCategoryModal = ({isOpen, onClose, category}: EditCategoryModalProps) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [create, {isPending: isCreating}] = useCreate();
  const [update, {isPending: isUpdating}] = useUpdate();
  const [updateFiles, {isPending: isUpdatingFiles}] = useUpdate();
  const [deleteFile, {isPending: isDeletingFile}] = useDelete();
  const desktopBannerImage = category?.files?.find(({fileType}) => fileType === CATEGORY_FILE_TYPE.BANNER_DESKTOP) ||
    category?.desktopBannerImage;
  const mobileBannerImage = category?.files?.find(({fileType}) => fileType === CATEGORY_FILE_TYPE.BANNER_MOBILE) ||
    category?.mobileBannerImage;

  const defaultValues = {
    ...category,
    desktopBannerImage: prepareFileInputValue(desktopBannerImage),
    mobileBannerImage: prepareFileInputValue(mobileBannerImage),
  };

  const createCategory = (data: any): Promise<string> => (
    new Promise((resolve, reject) => {
      create(
        'category',
        {data: {title: data.title, type: data.type}},
        {
          onSuccess: (response: any) => {
            const categoryId = response?.id || response?.data?.id;
            categoryId ? resolve(categoryId) : reject();
          },
          onError: reject,
        }
      );
    })
  );

  const updateCategory = (data: any): Promise<string> => (
    new Promise((resolve, reject) => {
      update(
        `category/${category!.id}`,
        {id: category!.id, data: {title: data.title, type: data.type}},
        {
          onSuccess: () => resolve(category!.id),
          onError: reject,
        }
      );
    })
  );

  const saveCategoryFile = (
    categoryId: string,
    formData: FormData,
    categoryFileId?: string,
  ): Promise<void> => (
    new Promise((resolve, reject) => {
      updateFiles(
        categoryFileId ? `category/${categoryId}/file/${categoryFileId}` : `category/${categoryId}/file`,
        {id: categoryId, data: formData},
        {
          onSuccess: () => resolve(),
          onError: reject,
        }
      );
    })
  );

  const deleteCategoryFile = (categoryId: string, categoryFileId: string): Promise<void> => (
    new Promise((resolve, reject) => {
      deleteFile(
        `category/${categoryId}/file`,
        {id: categoryFileId},
        {
          onSuccess: () => resolve(),
          onError: reject,
        }
      );
    })
  );

  const updateCategoryFiles = async (categoryId: string, data: any): Promise<void> => {
    const requests: Promise<void>[] = [];
    const desktopBannerImage = data.desktopBannerImage as CategoryFileValue;
    const mobileBannerImage = data.mobileBannerImage as CategoryFileValue;
    const initialDesktopBannerImage = defaultValues.desktopBannerImage;
    const initialMobileBannerImage = defaultValues.mobileBannerImage;
    const desktopFormData = prepareCategoryFileFormData(
      desktopBannerImage,
      CATEGORY_FILE_TYPE.BANNER_DESKTOP,
    );
    const mobileFormData = prepareCategoryFileFormData(
      mobileBannerImage,
      CATEGORY_FILE_TYPE.BANNER_MOBILE,
    );

    if (desktopFormData) {
      requests.push(saveCategoryFile(categoryId, desktopFormData, initialDesktopBannerImage?.id));
    } else if (initialDesktopBannerImage?.id && !desktopBannerImage) {
      requests.push(deleteCategoryFile(categoryId, initialDesktopBannerImage.id));
    }

    if (mobileFormData) {
      requests.push(saveCategoryFile(categoryId, mobileFormData, initialMobileBannerImage?.id));
    } else if (initialMobileBannerImage?.id && !mobileBannerImage) {
      requests.push(deleteCategoryFile(categoryId, initialMobileBannerImage.id));
    }

    await Promise.all(requests);
  };

  const handleSaveEdit = async (data: any) => {
    const shouldUpdateFiles = data.type === MATERIAL_TYPE.MATERIAL &&
      (hasFileChanges(data.desktopBannerImage, defaultValues.desktopBannerImage) ||
        hasFileChanges(data.mobileBannerImage, defaultValues.mobileBannerImage));

    try {
      const categoryId = category ? await updateCategory(data) : await createCategory(data);

      if (shouldUpdateFiles) {
        await updateCategoryFiles(categoryId, data);
      }

      notify(category ? "Категория обновлена" : "Категория создана", {type: "success"});
      refresh();
      onClose();
    } catch (error) {
      notify(category ? "Ошибка при обновлении категории" : "Ошибка при создании категории", {type: "error"});
    }
  };

  const title = category ? 'Редактировать категорию' : "Создать категорию"


  return (
    <Modal open={isOpen} onClose={onClose}>
      <Form defaultValues={defaultValues} onSubmit={handleSaveEdit}>
        <Typography variant="h6" mb={2}>
          {title}
        </Typography>
        <TextInput
          fullWidth
          source={'title'}
          label="Название категории"
          size={"small"}
          validate={[required(), validateSpace]}
        />
        <SelectInput
          validate={required()}
          size={"small"} fullWidth
          label={'Тип'}
          source="type"
          sx={{
            mt: 0
          }}
          choices={[
            {id: MATERIAL_TYPE.MATERIAL, name: MATERIAL_TYPE_TITLE[MATERIAL_TYPE.MATERIAL]},
            {id: MATERIAL_TYPE.KNOWLEDGE_BASE, name: MATERIAL_TYPE_TITLE[MATERIAL_TYPE.KNOWLEDGE_BASE]},
          ]}/>
        <FormDataConsumer>
          {({formData}) => formData.type === MATERIAL_TYPE.MATERIAL ? (
            <>
              <FileInput
                source="desktopBannerImage"
                label="Баннер для десктопа"
                accept={IMAGE_ACCEPT}
                multiple={false}
                validate={validateImage}
                sx={{
                  '& .RaFileInput-dropZone': {
                    backgroundColor: 'grey.100',
                    cursor: 'pointer',
                    '& > p': {
                      m: 1,
                    }
                  }
                }}>
                <FileField source="src" title="title"/>
              </FileInput>
              <FileInput
                source="mobileBannerImage"
                label="Баннер для мобилки"
                accept={IMAGE_ACCEPT}
                multiple={false}
                validate={validateImage}
                sx={{
                  '& .RaFileInput-dropZone': {
                    backgroundColor: 'grey.100',
                    cursor: 'pointer',
                    '& > p': {
                      m: 1,
                    }
                  }
                }}>
                <FileField source="src" title="title"/>
              </FileInput>
            </>
          ) : null}
        </FormDataConsumer>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button onClick={onClose}>Отмена</Button>
          <LoadingButton loading={isUpdating || isCreating || isUpdatingFiles || isDeletingFile} type={'submit'} variant="contained" color="primary"
                         sx={{ml: 2}}>
            Сохранить
          </LoadingButton>
        </Box>
      </Form>
    </Modal>
  )
}

export default EditCategoryModal
